import { Component, computed, inject, input, signal } from "@angular/core";
import { IonicModule } from "@ionic/angular";
import { ApiService } from "src/app/services/api/api.service";
import { ChannelData } from "src/app/interfaces/interfaces";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { addIcons } from "ionicons";
import { warning } from "ionicons/icons";
import { ModalStateService } from "src/app/services/modal-service/modal-state.service";

/**
 * Single cohesive config object replacing the previous 10 individual inputs.
 * The component exposes the same names internally via `computed()` accessors
 * so its logic stays unchanged.
 */
export interface LeaveMessageConfig {
  buttonName: string;
  resourceId: string;
  channel: ChannelData;
  team: string;
  messageId: string;
  connectorId: string;
  connectionId?: string;
  infoMessage?: string;
  isPortrait?: boolean;
  sendAsChannelMessage?: boolean;
}

@Component({
  selector: "app-leave-message-modal",
  templateUrl: "./leave-message-modal.component.html",
  styleUrls: ["./leave-message-modal.component.scss"],
  standalone: true,
  imports: [IonicModule, TranslateModule, FormsModule, CommonModule]
})
export class LeaveMessageModalComponent {
  readonly config = input.required<LeaveMessageConfig>();

  readonly buttonName = computed(() => this.config().buttonName);
  readonly resourceId = computed(() => this.config().resourceId);
  readonly channel = computed(() => this.config().channel);
  readonly team = computed(() => this.config().team);
  readonly messageId = computed(() => this.config().messageId);
  readonly connectorId = computed(() => this.config().connectorId);
  readonly connectionId = computed(() => this.config().connectionId);
  readonly infoMessage = computed(() => this.config().infoMessage);
  readonly isPortrait = computed(() => this.config().isPortrait);
  readonly sendAsChannelMessage = computed(() => this.config().sendAsChannelMessage ?? false);

  translateService: TranslateService;
  apiService: ApiService;
  modalState: ModalStateService;

  readonly leaveMessageStep = signal<
    "input" | "error" | "invalid"
  >("input");
  leaveMessageText: string = "";
  isSubmitting: boolean = false;

  constructor(
  ) {
    this.translateService = inject(TranslateService);
    this.apiService = inject(ApiService);
    this.modalState = inject(ModalStateService); 
    addIcons({
      warning
    });
  }

  onMessageChange(event: any): void {
    this.leaveMessageText = event.target.value;

    const validatedText = this.validateMessageText(this.leaveMessageText);
    if (validatedText !== this.leaveMessageText) {
      this.leaveMessageText = validatedText;
      this.leaveMessageStep.set("invalid");
    }
  }

  validateMessageText(text: string): string {    
    // Filter out potentially dangerous characters
    return text.replace(/[<>{}[\]/\\]/g, "");
  }

  async onSubmitMessage(): Promise<void> {
    if (!this.leaveMessageText.trim() || this.isSubmitting) return;
    this.isSubmitting = true;

    this.sendMessage().subscribe({
      next: () => {
        this.isSubmitting = false;
        this.modalState.triggerDismiss();
      },
      error: (error) => {
        console.error("[LeaveMessage] Message delivery failed", error?.status);
        this.isSubmitting = false;
        this.leaveMessageStep.set("error");
      }
    });
  }

  private sendMessage() {
    if (this.sendAsChannelMessage()) {
      return this.apiService.postTeamMessage(
        this.connectionId(),
        this.leaveMessageText,
        this.channel(),
        this.team(),
        null,
        this.infoMessage(),
        this.isPortrait(),
        this.connectorId()
      );
    }

    return this.apiService.postLeaveMessage(
      this.resourceId(),
      this.leaveMessageText,
      this.channel().id,
      this.team(),
      this.messageId(),
      this.buttonName(),
      this.connectorId()
    );
  }

  onRetryMessage(): void {
    this.leaveMessageStep.set("input");
  }

  onBackToInput(): void {
    this.leaveMessageStep.set("input");
  }

}